/**
 * Renders an action workflow module.
 * Shows the module header (name, description, hero image) and a list
 * of actions with execution state, using the existing ActionExecutionList.
 */
import type { Module } from "@nucleus/shared-types";
import { useState } from "react";

import { ActionExecutionList } from "@/components/actions/ActionExecutionRenderer";
import { trpc } from "@/lib/trpc";

interface WorkflowModulePageProps {
	module: Module;
}

/**
 * Workflow module page — renders action steps for a module.
 * @param props - component props
 * @param props.module - the resolved module record
 */
export function WorkflowModulePage({ module }: WorkflowModulePageProps) {
	const utils = trpc.useUtils();
	const [loadingActionId, setLoadingActionId] = useState<string | undefined>();

	// Fetch module progress (actions + execution state)
	const progressQuery = trpc.actions.getModuleProgress.useQuery({
		moduleId: module.id,
	});

	// Start action mutation
	const startActionMutation = trpc.actions.startAction.useMutation({
		onMutate: ({ moduleActionId }) => {
			setLoadingActionId(moduleActionId);
		},
		onSettled: () => {
			setLoadingActionId(undefined);
			void utils.actions.getModuleProgress.invalidate({ moduleId: module.id });
		},
	});

	// Complete action mutation
	const completeActionMutation = trpc.actions.completeAction.useMutation({
		onMutate: ({ moduleActionId }) => {
			setLoadingActionId(moduleActionId);
		},
		onSettled: () => {
			setLoadingActionId(undefined);
			void utils.actions.getModuleProgress.invalidate({ moduleId: module.id });
		},
	});

	// Skip action mutation
	const skipActionMutation = trpc.actions.skipAction.useMutation({
		onSettled: () => {
			void utils.actions.getModuleProgress.invalidate({ moduleId: module.id });
		},
	});

	/**
	 * Handle starting an action.
	 * @param actionId - the module action ID to start
	 */
	const handleStartAction = (actionId: string) => {
		startActionMutation.mutate({ moduleActionId: actionId });
	};

	/**
	 * Handle completing an action with optional data from the plugin's ExecutionView.
	 * The plugin data is passed through as-is; the API accepts it as an optional object.
	 * @param actionId - the module action ID to complete
	 * @param data - execution data from the plugin component
	 */
	const handleCompleteAction = (actionId: string, data: unknown) => {
		completeActionMutation.mutate({
			moduleActionId: actionId,
			data: data as Parameters<typeof completeActionMutation.mutate>[0]["data"],
		});
	};

	/**
	 * Handle skipping an optional action.
	 * @param actionId - the module action ID to skip
	 * @param reason - optional reason for skipping
	 */
	const handleSkipAction = (actionId: string, reason?: string) => {
		skipActionMutation.mutate({ moduleActionId: actionId, reason });
	};

	return (
		<div className="p-6">
			{/* Module Header */}
			<header className="mb-6">
				{module.heroImage && (
					<div className="mb-4 overflow-hidden rounded-lg">
						<img
							src={module.heroImage}
							alt={module.name}
							className="h-48 w-full object-cover"
						/>
					</div>
				)}
				<h1 className="text-2xl font-bold">{module.name}</h1>
				{module.description && (
					<p className="mt-1 text-muted-foreground">{module.description}</p>
				)}
			</header>

			{/* Loading State */}
			{progressQuery.isLoading && (
				<div className="flex min-h-[200px] items-center justify-center">
					<div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
				</div>
			)}

			{/* Error State */}
			{progressQuery.error && (
				<div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-red-400">
					Failed to load module content: {progressQuery.error.message}
				</div>
			)}

			{/* Action List */}
			{progressQuery.data && (
				<>
					{progressQuery.data.actions.length === 0 ? (
						<div className="rounded-lg border border-dashed p-12 text-center text-muted-foreground">
							This module has no actions configured yet.
						</div>
					) : (
						<ActionExecutionList
							// Normalize actionType: API returns undefined when type is not found,
							// but ActionExecutionList expects null for the "no type" case.
							actions={progressQuery.data.actions.map((a) => ({
								...a,
								actionType: a.actionType ?? null,
							}))}
							onStartAction={handleStartAction}
							onCompleteAction={handleCompleteAction}
							onSkipAction={handleSkipAction}
							loadingActionId={loadingActionId}
						/>
					)}
				</>
			)}
		</div>
	);
}
